import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import React, { useEffect, useState } from "react";
import { getLocalSystemDate, parseIsoDateToMDYUS } from "../../utils/dateUtils";
import { buildPayloadWithIsoDates } from "../../utils/applicationFormPayload";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import { request } from "../../utils/api";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import { identificationCardSchema } from "./application.validation";
import ApplicationFormFooter from './ApplicationFormFooter';
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";


const IdentificationCard = ({ positionName, onClose }) => {
  const [cardId, setCardId] = useState(null);

  const methods = useForm({
    resolver: zodResolver(identificationCardSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      employeeName: "",
      applicantSignature: "",
      applicantSignedAt: "",
      formDate: parseIsoDateToMDYUS(getLocalSystemDate()),
      replacementFeeAmount: 15.00,
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: "",
      positionAppliedFor: positionName || "",
      applicantStatementAccepted: false
    },
    mode: "onChange"
  });

  const { handleSubmit, setValue, watch, reset } = methods;

  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const payload = buildPayloadWithIsoDates(
        data,
        additionalData,
        ["formDate", "applicantSignedAt", "reviewedBySignedAt"],
        { flag: actualFlag }
      );

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      if (cardId) {
        payload.cardId = cardId;
      }

      let response;
      if (cardId) {
        response = await request(`/identification-cards/${cardId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        response = await request('/identification-cards', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      if (response.status === 1) {
        toast.success(response.message || "Identification Card processed successfully!");
        closeForm();
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error processing form:", error);
      toast.error(`Failed to process form: ${error.message}`);
    }
  };

  const {
    handleStatusAction,
    handleSave,
    targetUserId,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,


    actualFromId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "employeeName",
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  useEffect(() => {
    if (positionName) {
      setValue("positionAppliedFor", positionName);
    }
  }, [positionName, setValue]);

  useEffect(() => {
    const fetchIdentificationCard = async () => {
      // Must wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId && !cardId) return;

      try {
        if (!actualFromId && !targetUserId && !cardId) {
          if (currentUserData && !isAdmin) {
            reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              employeeName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              formDate: parseIsoDateToMDYUS(getLocalSystemDate()),
              positionAppliedFor: positionName || "",
              replacementFeeAmount: 15.00,
              applicantStatementAccepted: false
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/identification-cards/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/identification-cards?userId=${targetUserId}`);
          }
        } else {
          response = await request(`/identification-cards/${actualFromId || targetUserId}`);
        }

        let data = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) {
              data = response.result[0];
            }
          } else if (response.result?.card) {
            data = response.result.card;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            if (response.result.items.length > 0) {
              data = response.result.items.find(item => item.userId === Number.parseInt(targetUserId, 10));
            }
          }
        }

        if (data) {
          setCardId(data.id);
          setCurrentStatus(data.status || "");
          reset({
            ...data,
            employeeName: data.employeeName || data.applicantName || "",
            applicantSignature: data.applicantSignature || data.employeeSignature || "",
            ...getStandardizedSignatures({ ...data, applicantSignatureDate: data.employeeSignatureDate }, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(getStandardizedSignatures({ ...data, applicantSignatureDate: data.employeeSignatureDate }, isAdmin).applicantSignedAt || getLocalSystemDate()),
            reviewedBySignedAt: parseIsoDateToMDYUS(getStandardizedSignatures({ ...data, applicantSignatureDate: data.employeeSignatureDate }, isAdmin).reviewedBySignedAt || ""),
            formDate: parseIsoDateToMDYUS(data.formDate || getLocalSystemDate())
          });
        }
      } catch (error) {
        console.error("Error fetching identification card:", error);
      }
    };

    fetchIdentificationCard();
  }, [targetUserId, reset, currentUserData, isAdmin, cardId, actualFromId, positionName]);







  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <EvaluationFormHeader title="Identification Card" />
        <div className="p-8">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <CorrectionRequestAlert status={currentStatus} comments={methods.watch("comments")} />
              <fieldset disabled={isReadOnly} className="space-y-6">
                <div className="mb-8 mt-6">
                  <p className="text-sm leading-relaxed text-gray-800">
                    This statement is to acknowledge receipt of our agency's
                    Identification Card. Due to the cost in producing these cards,
                    the agency will be imposing a $15.00 replacement fee for lost
                    cards.
                  </p>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Employee Name - Left Side */}
                    <div>
                      <EvaluationInput
                        name="employeeName"
                        label="Employee Name:"
                        placeholder="Enter Employee Name"
                      />
                    </div>

                    {/* Date Field */}
                    <div>
                      <InputDatePicker
                        formatSeparator="/"
                        name="formDate"
                        label="Date:"
                        max={getLocalSystemDate()}
                      />
                    </div>
                  </div>
                </div>
                <ApplicationFormFooter
                  page={1}
                  setPage={() => { }}
                  totalPages={1}
                  methods={methods}
                  onSave={handleSave}
                  onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                  onStatusUpdate={handleStatusAction}
                  isSignatureModalOpen={isSignatureModalOpen}
                  openSignatureModal={openSignatureModal}
                  closeSignatureModal={closeSignatureModal}
                  handleSignatureApply={handleSignatureApply}
                  signerName={signerName}
                  showCertification={true}
                  requireSignatures={true}
                  showSignatureSections={true}
                  applicantName={watch("employeeName") || ""}
                  applicantSignature={watch("applicantSignature") || ""}
                  applicantSignedAt={watch("applicantSignedAt") || ""}
                  reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                  reviewedBySignature={watch("reviewedBySignature") || ""}
                  reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                  submitLabel="Submit & Print"
                  onClose={closeForm}
                  isReadOnly={isReadOnly}
                  currentStatus={currentStatus}
                />

              </fieldset>
            </form>


          </FormProvider>
        </div>
      </div>
    </>
  );
};

export default IdentificationCard;
